<template>
  <div>
    <slot name="activator" :on="on"></slot>

    <portal to="modals" :disabled="noTarget">
      <transition name="modal">
        <div class="modal" :class="setClassModifiers('modal--')" v-if="show">
          <div class="modal__bg" @click="handleClickBg ? show = false : null" v-if="!noBackground"></div>
          <div ref="modalInner" class="modal__inner d-flex direction-column" :style="{
            minWidth: computedWidth,
            margin: margin + 'px',
            bottom: position === 'bottom' ? '0' : null,
            maxHeight: computedMaxHeight,
            borderTopLeftRadius: maxHeight || fitContent ? '1rem' : null,
            borderTopRightRadius: maxHeight || fitContent? '1rem' : null
          }">
            <div class="modal__close" @click="show = false" :style="{ top: computedCloseTop + 'px' }" v-if="!unclosable">
              <span :style="typeof headerTopIndent !== 'undefined' ? 'opacity: 1' : 'opacity: 0'" class="icon-close"></span>
            </div>
            <div ref="modalHeader" v-if="$slots.header" class="modal__header" :style="{ paddingTop: computedHeaderPaddingTop + 'px' }" :class="unclosable ? 'modal__header--unclosable' : ''">
              <div :style="typeof headerTopIndent !== 'undefined' ? 'opacity: 1' : 'opacity: 0'">
                <slot name="header"></slot>
              </div>
            </div>

            <div
                ref="modalContainer"
                class="modal__container"
                @scroll="onScroll"
                :style="{ height: '100%', maxHeight: containerHeight + 'px', height: full ? containerHeight + 'px' : '' }"
                v-scrollable
            >
              <div class="modal__main" ref="modalMain">
                <slot :containerHeight="containerHeight"></slot>
              </div>
            </div>

            <div ref="modalFooter" v-if="$slots.footer" class="modal__footer">
              <div class="modal__footer-inner">
                <slot name="footer" :modalFooterHeight="modalFooterHeight" :close="close"></slot>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </portal>
  </div>
</template>

<script>
import setClassModifiers from '@/mixins/setClassModifiers'
import modalActivate from '@/mixins/modalActivate'
import screen from '@/mixins/screen'
import safeArea from '@/mixins/safeArea'

import scrollable from '@/directives/scrollable'

let computingHeight = false

export default {
  mixins: [setClassModifiers, modalActivate, screen, safeArea],

  directives: {
    scrollable
  },

  props: {
    /**
     * Removes background layer if true
     */
    noBackground: {
      type: Boolean,
      default: false
    },

    noTarget: {
      type: Boolean,
      default: false
    },

    handleClickBg: {
      type: Boolean,
      default: false
    },

    /**
     * @values slide-down, slide-up
     */
    animation: {
      type: String,
      default: 'modal'
    },
    noPadding: {
      type: Boolean,
      default: false
    },
    noHeaderPadding: {
      type: Boolean,
      default: false
    },
    minWidth: {
      type: String,
      default: '35rem'
    },
    /**
     * Removes close button
     */
    unclosable: {
      type: Boolean,
      default: false
    },

    full: {
      type: Boolean,
      default: false
    },

    blockScroll: {
      type: Boolean,
      default: false
    },
    /**
     * @values top, bottom
     */
    position: {
      type: String,
      default: 'top'
    },

    maxHeight: {
      type: String,
      default: null
    },

    fitContent: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      modalHeaderHeight: 0,
      modalFooterHeight: 0,
      modalMainHeight: 0,

      headerTopIndent: undefined
    }
  },

  computed: {
    computedHeaderPaddingTop () {
      if (this.noHeaderPadding) {
        return 0
      }
      if ((this.headerTopIndent && this.headerTopIndent <= this.safeArea.top) || (!this.fitContent && this.full)) {
        return this.safeArea.top > 17.5 ? this.safeArea.top + 15 : 17.5
      }

      return 17.5
    },

    computedCloseTop () {
      if (this.noHeaderPadding) {
        return 0
      }
      if ((this.headerTopIndent && this.headerTopIndent <= this.safeArea.top) || (!this.fitContent && this.full)) {
        return (this.safeArea.top - 3.5) > 14 ? this.safeArea.top + 11.5 : 14
      }

      return 14
    },

    computedMaxHeight () {
      if (this.fitContent) {
        return (this.modalMainHeight + 15 + this.modalFooterHeight + this.modalHeaderHeight) + 'px'
      }

      return this.maxHeight
    },

    aggregateHeight () {
      return this.modalMainHeight + 15 + this.modalFooterHeight + this.modalHeaderHeight
    },

    computedWidth () {
      if (this.full) {
        return '100%'
      }

      return this.minWidth
    },

    margin () {
      if (this.full) {
        return 0
      }

      return 30
    },

    containerHeight () {
      const modalMainHeight = this.modalMainHeight

      if (this.fitContent && this.aggregateHeight < this.screen.height) {
        return modalMainHeight + 15
      }

      return this.screen.height - this.modalHeaderHeight - this.modalFooterHeight - (this.margin * 2)
    }
  },

  methods: {
    computeHeights () {
      this.$nextTick(() => {
        const modalHeader = this.$refs.modalHeader
        const modalFooter = this.$refs.modalFooter
        const modalMainHeight = this.$refs.modalMain

        modalHeader ? this.modalHeaderHeight = modalHeader.clientHeight : this.modalHeaderHeight = 0
        modalFooter ? this.modalFooterHeight = modalFooter.clientHeight : this.modalFooterHeight = 0
        modalMainHeight ? this.modalMainHeight = modalMainHeight.clientHeight : this.modalMainHeight = 0
      })
    },

    onScroll (e) {
      const scrollHeight = Math.round(e.target.scrollHeight)
      const scrollTop = Math.round(e.target.scrollTop)
      const contentHeight = Math.round(e.target.clientHeight)

      if (scrollHeight === (scrollTop + contentHeight + 1) || scrollHeight === (scrollTop + contentHeight - 1) || scrollHeight === (scrollTop + contentHeight)) {
        const event = new Event('reachedBottom')
        document.dispatchEvent(event)
      }
      this.$emit('scroll', e)
    }
  },

  async mounted () {
    this.$nextTick(() => {
      this.computeHeights()
    })
    if (this.show === true && this.blockScroll === true) {
      this.$locker.lock('body', {
        reserveScrollBarGap: true
      })
    }
    if (this.show === true) {
      setTimeout(() => {
        const el = this.$refs.modalInner.getBoundingClientRect()
        this.headerTopIndent = el.top
      }, 300)
    }
  },

  updated () {
    if (!computingHeight) {
      setTimeout(() => {
        this.computeHeights()
        computingHeight = false
      }, 50)
    }
    computingHeight = true
  },

  watch: {
    show (val) {
      if (val === true && this.blockScroll === true) {
        this.$locker.lock('body', {
          reserveScrollBarGap: true
        })
      } else {
        /** istanbul ignore next */
        setTimeout(() => {
          this.$locker.unlockAll()
        }, 100)
      }

      if (val && !this.fitContent) {
        const appContainer = document.querySelector('.app-container')

        if (appContainer && this.full) {
          appContainer.classList.add('app', 'app--translate')
        }
      } else if (!this.fitContent) {
        const appContainer = document.querySelector('.app-container')
        if (appContainer && this.full) {
          appContainer.classList.remove('app--translate')
        }
      }

      this.computeHeights()
      setTimeout(() => {
        this.computeHeights()
      }, 100)

      if (val) {
        setTimeout(() => {
          const el = this.$refs.modalInner.getBoundingClientRect()
          this.headerTopIndent = el.top
        }, 300)
      }
    }
  }
}
</script>

<style lang="scss">
  .modal {
    position: fixed;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    z-index: 99999;

    @include computedVh(100);

    &__bg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 9999;
      background-color: $color-black;
      opacity: .35;
    }

    &__inner {
      background-color: $color-white;
      position: absolute;
      margin-right: auto;
      z-index: 99999;
      border-radius: $border-radius;
      overflow: hidden;
    }

    &__close {
      position: absolute;
      font-size: 2.2rem;
      color: $color-grey;
      right: 1.5rem;
      cursor: pointer;
      z-index: 999999;

      & > * {
        transition: .2s;
      }

      &:active {
        -webkit-tap-highlight-color: transparent;
      }
    }

    &--full &__inner {
      border-radius: 0;
    }

    &__container {
      overflow-y: auto;
    }

    &__header,
    &__main,
    &__footer-inner
    {
      padding-top: 17.5px;
      padding-bottom: 17.5px;

      @include defaultSpacing
    }

    &--no-header-padding {
      .modal__header {
        padding: 0;
      }
    }

    &--no-padding &__main {
      padding: 0;
    }

    &__header {
      background-color: $color-white;
      font-weight: 500;
      border-bottom: 1px $color-space-lighten solid;
      padding-top: 17.5px;

      & > * {
        transition: .2s;
      }
    }

    &__footer {
      background-color: $color-white;
      border-top: 1px #EAEAEA solid;
      z-index: 1;

      &-inner {
        @include iosPaddingBottom(1.75rem)
      }
    }
  }
</style>
